import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { FullCalendarComponent } from '../../common/full-calendar/full-calendar.component';
import { MatterSearchComponent } from '../../common/matter-search/matter-search.component';

import { Event } from '../../../models/event';

import { EventService } from '../../../services/events/event.service';
import { NotyService } from '../../../services/common/noty.service';
import { QueryParamsService } from '../../../services/common/query-params.service';

declare var $: any;
declare var moment: any;
declare var window: any;

@Component({
  selector: 'app-events-index',
  templateUrl: './events-index.component.html',
  styleUrls: ['./events-index.component.css'],
})
export class EventsIndexComponent implements OnInit, OnDestroy {
  /**
   * Id of event being edited
   * @type {number | undefined}
   */
  editingEventId: number | undefined;

  /**
   * Modal object for edit modal
   * @type {BsModalRef}
   */
  editModalRef: BsModalRef | undefined;

  /**
   * Event form title
   * @type {string | undefined}
   */
  eventFormTitle: string | undefined;

  /**
   * Indicates if event show modal has edit option
   * @type {boolean}
   */
  eventShowReadonly: boolean = false;

  /**
   * Array of events from full calendar
   * @type {Array<any>}
   */
  events: Array<any> = [];

  /**
   * Id for event show modal
   * @type {number | undefined}
   */
  eventShowId: number | undefined;

  /**
   * Date in filter
   */
  filterDate: Date = new Date();

  /**
   * Selected filter list header
   * @type {string}
   */
  filterItemsListHeader: string | null;

  /**
   * List of items in selected filter list
   * @type {Array<any>}
   */
  filterItems: Array<any> = [];

  /**
   * TODO: Not in use, should be checked and removed
   * @type {string | null}
   */
  filterListName: string | null;

  /**
   * Filter date as moment
   * @type {any}
   */
  filterMomentDate: any;

  /**
   * Filter options from backend
   * @type {any}
   */
  filterOptions: any = {
    attorneys: [],
    event_types: [],
    locations: [],
    staff: [],
    company: true,
    placekeeper: true,
    recurring: true,
    timezone_offset: -300,
  };

  filterOptionsCheckInterval: any;

  filterOptionsLoaded: boolean = false;

  initialView: string = '';

  /**
   * Matter id for new form
   * @type {number}
   */
  matterId: number;

  /**
   * Number of multi day events to show
   * @type {number}
   */
  multiDayNumberOfDays: number = 14;

  /**
   * Multi day options
   * @type {Array<number>}
   */
  multiDayOptions: Array<number> = [14, 28, 50, 75, 100];

  newEventDate: string;

  /**
   * Route events subscription to get change in query params for current page
   * @type {Subscription}
   */
  queryParamsSubscription: Subscription;

  /**
   * Show filter
   * @type {boolean}
   */
  showFilter: boolean = true;

  /**
   * Show selected filter list
   * @type {boolean}
   */
  showFilterItemsList: boolean = false;

  /**
   * Indicates if an event was being viewed in modal before it was being edited
   * @type {boolean}
   */
  showingEvent: boolean = false;

  /**
   * Modal object for event show
   * @type {BsModalRef}
   */
  showModalRef: BsModalRef;

  /**
   * View mode for events
   * @type {string}
   */
  viewName: string = 'agendaDay';

  /**
   * Container element ref for scroll adjust
   * @param {[type]} 'container' HTML element variable
   */
  @ViewChild('container', { static: true }) containerElementRef: ElementRef;

  /**
   * Event form template ref
   * @param {[type]} 'event_form' variable in HTML
   */
  @ViewChild('event_form', { static: true }) eventFormTemplateRef: TemplateRef<any>;

  /**
   * Event show template ref
   * @param {[type]} 'event_show' variable in HTML
   */
  @ViewChild('event_show', { static: true }) eventShowTemplateRef: TemplateRef<any>;

  /**
   * Filter container element ref for scroll adjust
   * @param {[type]} 'filterContainer' HTML element variable
   */
  @ViewChild('filterContainer', { static: true }) filterContainerElementRef: ElementRef;

  /**
   * Selected filter list container ref for scroll adjust
   * @param {[type]} 'filterItemsContainer' HTML element variable
   */
  @ViewChild('filterItemsContainer', { static: true }) filterItemsContainerElementRef: ElementRef;

  /**
   * Full calendar component
   * @param {[type]} 'fullCalendar' variable in HTML
   */
  @ViewChild('fullCalendar', { static: true }) fullCalendarComponent: FullCalendarComponent;

  /**
   * Matter search component
   * @param {[type]} 'matterSearch' Variable in HTML
   */
  @ViewChild('matterSearch', { static: true }) matterSearchComponent: MatterSearchComponent;

  /**
   * Date picker ref
   * @param {[type]} 'reportrange' Variable in HTML
   */
  @ViewChild('reportrange', { static: true }) datePickerElementRef: ElementRef;

  /**
   * Constructor
   * @param {EventService}   private eventService Service to access events resource in backend
   * @param {BsModalService} private modalService Modal service from ngx-bootstrap
   */
  constructor(private activatedRoute: ActivatedRoute, private eventService: EventService, private modalService: BsModalService, private notyService: NotyService, private queryParamsService: QueryParamsService, private router: Router) {
    this.filterOptions = this.defaultFilterOptions();
  }

  // Component Lifecycle events

  /**
   * Called when component is initialized
   */
  ngOnInit() {
    this.eventService.searchOptions().then((response_json) => {
      if (response_json && response_json.event_types && response_json.attorneys && response_json.staff && response_json.locations) {
        this.filterOptions = response_json;
        this.filterOptionsLoaded = true;
      } else {
        this.filterOptions = this.defaultFilterOptions();
        this.notyService.notify('Error loading filter options, please notify tech if this persists after page reload', 'error');
      }

      if (response_json.view_name) {
        this.viewName = response_json.view_name;
        this.initialView = this.viewName;
        this.setSearchParams();
        let scope: EventsIndexComponent = this;
        window.setTimeout(() => {
          scope.updateMultiday();
        }, 500);
      }
    });
    this.dateChange(this.filterDate, true);
    this.queryParamsSubscription = this.activatedRoute.queryParams.subscribe((queryParams) => {
      window.setTimeout(() => {
        if (queryParams.edit_event_id) {
          this.editingEventId = queryParams.edit_event_id;
          this.openEditModal(this.eventFormTemplateRef);
        } else if (queryParams.show_event_id) {
          this.showingEvent = true;
          this.eventShowId = queryParams.show_event_id;
          this.openShowModal(this.eventShowTemplateRef);
        } else if (queryParams.new_event && queryParams.matter_id) {
          this.matterId = queryParams.matter_id;
          this.newEvent();
        } else if (queryParams.new_event) {
          if (queryParams.date) this.newEventDate = queryParams.date;
          if (!this.filterOptionsLoaded) {
            let scope: EventsIndexComponent = this;
            this.filterOptionsCheckInterval = window.setInterval(() => {
              if (scope.filterOptionsLoaded) {
                window.clearInterval(scope.filterOptionsCheckInterval);
                scope.newEvent();
              }
            }, 200);
          } else this.newEvent();
        }
      }, 100);
    });
  }

  /**
   * Called when component is destroyed
   */
  ngOnDestroy() {
    if (this.editModalRef && this.editModalRef.hide) this.editModalRef.hide();
    if (this.showModalRef && this.showModalRef.hide) this.showModalRef.hide();
    if (this.queryParamsSubscription) this.queryParamsSubscription.unsubscribe();
  }

  // Event Listeners

  /**
   * Listener to window scroll
   * @param {scroll'} 'window  window
   * @param {[type]}  '$event' event
   */
  @HostListener('window:scroll', ['$event'])
  window_scroll(event: any) {
    this.content_sidebar_scroll();
  }

  /**
   * Listener to window resize
   * @param {scroll'} 'window  window
   * @param {[type]}  '$event' event
   */
  @HostListener('window:resize', ['$event'])
  window_resize(event: any) {
    this.content_sidebar_scroll();
  }

  // Component methods

  checkInitialView() {
    if (this.initialView == 'multi-day' || this.initialView == 'agnostic') {
      this.loadEvents();
      this.initialView = '';
    }
  }

  /**
   * Clear all filter options
   */
  clearAll() {
    this.filterOptions.company = true;
    this.filterOptions.inactive = false;
    this.filterOptions.recurring = false;
    this.filterOptions.shadow = false;
    this.filterOptions.placekeeper = false;
    if (this.filterOptions.attorneys) for (let attorney of this.filterOptions.attorneys) attorney.selected = false;
    if (this.filterOptions.event_types) for (let event_type of this.filterOptions.event_types) event_type.selected = false;
    if (this.filterOptions.locations) for (let location of this.filterOptions.locations) location.selected = false;
    if (this.filterOptions.staff) for (let staff_member of this.filterOptions.staff) staff_member.selected = false;
    this.matterSearchComponent.clearSelectedMatter();
    this.filterOptions.matter_id = null;
    this.loadEvents();
  }

  /**
   * Date change in filter
   * @param {Date} date new date
   */
  dateChange(date: Date, initialCall: boolean = false) {
    if (!date) return;
    let addWeek: boolean = false;
    let addDay: boolean = false;
    if (!this.isMultiDayView()) {
      addWeek = this.viewName == 'agendaDay';
      addDay = this.viewName == 'agendaWeek';
    } else addDay = true;
    this.filterDate = date;
    let momentDate: any = moment(date);
    this.filterMomentDate = momentDate;
    let datePickerElement: HTMLElement = this.datePickerElementRef.nativeElement;
    datePickerElement.getElementsByClassName('dateranger')[0].innerHTML = momentDate.format('MMMM' + (addDay ? ' Do' : '') + ', YYYY ' + (addWeek ? '- Wo' : '')) + (addWeek ? ' week' : '');
    if (this.isMultiDayView()) {
      this.updateMultiday();
      if (initialCall) this.fullCalendarGotoDate(momentDate);
    } else {
      this.fullCalendarGotoDate(momentDate);
    }
  }

  /**
   * Default filter options
   * @return {any} [description]
   */
  defaultFilterOptions(): any {
    return {
      attorneys: [],
      event_types: [],
      locations: [],
      staff: [],
      company: true,
      placekeeper: true,
      recurring: true,
      timezone_offset: -300,
    };
  }

  /**
   * Select a filter list
   * @param {string} listName Name of list to show
   */
  filterListSelect(listName: string | null) {
    if (this.showFilterItemsList && (!listName || this.filterListName == listName)) {
      this.showFilterItemsList = false;
      this.filterItemsListHeader = null;
      this.filterItems = [];
    } else {
      this.showFilterItemsList = true;
      this.filterListName = listName;
      switch (listName) {
        case 'attorneys':
          this.filterItemsListHeader = 'Attorneys';
          this.filterItems = this.filterOptions.attorneys;
          break;
        case 'event_types':
          this.filterItemsListHeader = 'Event Types';
          this.filterItems = this.filterOptions.event_types;
          break;
        case 'locations':
          this.filterItemsListHeader = 'Locations';
          this.filterItems = this.filterOptions.locations;
          break;
        case 'staff':
          this.filterItemsListHeader = 'Staff';
          this.filterItems = this.filterOptions.staff;
          break;
      }
      let scope: EventsIndexComponent = this;
      window.setTimeout(() => {
        scope.content_sidebar_scroll();
      }, 500);
    }
  }

  /**
   * Process form result
   * @param {string} result Form result
   */
  formResult(result: string) {
    if (result != 'cancelled') {
      this.loadEvents();
      this.updateMultiday();
    }
    if (this.activatedRoute.snapshot.queryParams.matter_id) {
      this.router.navigateByUrl(`/matters/${this.activatedRoute.snapshot.queryParams.matter_id}`);
      return;
    }
    if (this.editModalRef) {
      if (this.editModalRef.hide) this.editModalRef.hide();
      this.editModalRef = undefined;
    }
    if (result == 'save_close') {
      this.showingEvent = false;
      if (this.showModalRef) this.showModalRef.hide();
      return;
    }
    if (this.showingEvent) this.openShowModal(this.eventShowTemplateRef);
  }

  fullCalendarGotoDate(momentDate: any) {
    if (this.fullCalendarComponent && this.fullCalendarComponent.calendar && momentDate) {
      console.log('Goto date being called for fullcalendar');
      this.fullCalendarComponent.calendar.gotoDate(momentDate.toDate());
    } else {
      let scope: EventsIndexComponent = this;
      window.setTimeout(() => {
        if (scope.fullCalendarComponent && scope.fullCalendarComponent.calendar && momentDate) {
          console.log('Goto date being called for fullcalendar');
          scope.fullCalendarComponent.calendar.gotoDate(momentDate.toDate());
        }
      }, 3000);
    }
  }

  /**
   * Load events with a delay for any changes to pass to child components
   */
  loadEvents() {
    window.setTimeout(() => {
      this.fullCalendarComponent.refreshView();
      this.updateMultiday();
    }, 200);
  }

  isMultiDayView(): boolean {
    return this.viewName == 'multi-day' || this.viewName == 'agnostic';
  }

  /**
   * Open new event modal
   */
  newEvent() {
    this.eventFormTitle = 'New Event';
    if (this.editingEventId && this.editingEventId > 0) this.editingEventId = undefined;
    this.openEditModal(this.eventFormTemplateRef);
  }

  /**
   * Open Edit Modal
   * @param {TemplateRef<any>} template Template to show
   * @param {false         }}  config   Config for Bs Modal with a default value
   */
  openEditModal(template: TemplateRef<any>, config: any = { backdrop: true, class: 'modal-lg', ignoreBackdropClick: true }) {
    if (this.editModalRef && this.editModalRef.hide) this.editModalRef.hide();
    this.editModalRef = this.modalService.show(template, config);
  }

  /**
   * Open Show Modal
   * @param {TemplateRef<any>} template Template to show
   * @param {false         }}  config   Config for Bs Modal with a default value
   */
  openShowModal(template: TemplateRef<any>, config: any = { backdrop: true, ignoreBackdropClick: true }) {
    if (this.showModalRef) this.showModalRef.hide();
    this.showModalRef = this.modalService.show(template, config);
  }

  /**
   * Set search params from query params
   */
  setSearchParams() {
    // TODO: Edit filter options as per search options
    let queryParams: any = this.queryParamsService.retrieveQueryParams(this.defaultFilterOptions(), this.activatedRoute);
    let keys: Array<Array<string>> = [
      ['event_attorneys', 'attorneys'],
      ['event_staff', 'staff'],
      ['event_locations', 'locations'],
      ['event_types', 'event_types'],
    ];
    for (let paramKey of keys) if (queryParams[paramKey[0]]) for (let filterItem of this.filterOptions[paramKey[1]]) if (queryParams[paramKey[0]].indexOf(filterItem.id) > -1) filterItem.selected = true;
    this.filterOptions.matter_id = this.filterOptions.matter_id || queryParams.event_matter_number;
    this.filterOptions.inactive = String(queryParams.inactive) == 'true';
    this.filterOptions.recurring = String(queryParams.recurring) == 'true';
    this.filterOptions.company = String(queryParams.company) == 'true';
    this.filterOptions.shadow = String(queryParams.shadow) == 'true';
    this.filterOptions.placekeeper = String(queryParams.placekeeper) == 'true';
  }

  /**
   * Toggle filter to show or hide
   */
  toggleFilter() {
    this.showFilter = !this.showFilter;
    let scope: EventsIndexComponent = this;
    window.setTimeout(() => {
      scope.content_sidebar_scroll();
    }, 100);
  }

  /**
   * Select item in filter list
   * @param {any} item Item to select
   */
  toggleItemSelection(item: any) {
    item.selected = !item.selected;
    this.loadEvents();
  }

  /**
   * Multiday number of days update
   */
  updateMultiday() {
    if (this.isMultiDayView()) {
      if (!this.filterMomentDate) this.filterMomentDate = moment().startOf('day');
      else this.filterMomentDate = this.filterMomentDate.startOf('day');
      this.fullCalendarComponent.updateEvents(this.filterMomentDate, moment(this.filterMomentDate).add(this.multiDayNumberOfDays, 'days'), null, () => {}, true, this.viewName == 'agnostic');
    }
  }

  // Functions for UI manipulation using vanilla JS or jQuery

  /**
   * Function to handle scroll or resize for filter HTML
   */
  content_sidebar_scroll() {
    let containerElement: HTMLElement = this.containerElementRef.nativeElement;
    let filterContainerElement: HTMLElement = this.filterContainerElementRef.nativeElement;
    let filterItemsContainerElement: HTMLElement = this.filterItemsContainerElementRef.nativeElement;
    let listContainer: any = filterContainerElement.getElementsByClassName('list-group')[0];
    let itemListContainer: any = filterItemsContainerElement.getElementsByClassName('list-group')[0];
    let sidebar_height: number = window.innerHeight - 89;
    if (containerElement && filterContainerElement && listContainer) {
      if (containerElement.getBoundingClientRect().top > 19) {
        sidebar_height -= containerElement.getBoundingClientRect().top;
        //console.log('greater ' + matter_sidebar.style.top);
      }
      if (listContainer.clientHeight != sidebar_height) {
        listContainer.style.height = sidebar_height + 'px';
      }
      if (itemListContainer && itemListContainer.clientHeight != sidebar_height) {
        itemListContainer.style.height = sidebar_height + 'px';
      }
    }
  }
}
